import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  BusinessModel, 
  Template, 
  TemplateCategory, 
  PREDEFINED_BUSINESS_MODELS,
  BusinessModelId 
} from '../types/template';
import { 
  AMAZON_FBA_TEMPLATE, 
  SHOPIFY_DIRECT_IMPORT_TEMPLATE, 
  DROPSHIPPING_PER_ORDER_TEMPLATE, 
  PORT_AGENT_VESSEL_CALL_TEMPLATE, 
  PORT_AGENT_CONTAINER_VESSEL_TEMPLATE, 
  PORT_AGENT_TANKER_VESSEL_TEMPLATE, 
  PORT_AGENT_BULK_CARRIER_TEMPLATE, 
  AMAZON_WHOLESALE_DOMESTIC_TEMPLATE,
  INDUSTRIAL_EQUIPMENT_INTERNATIONAL_TEMPLATE,
  CHEMICAL_RAILWAY_TRANSPORT_TEMPLATE,
  AUTOMOTIVE_CONTAINER_EXPORT_TEMPLATE,
  CONSTRUCTION_PROJECT_TEMPLATE,
  INDUSTRIAL_DOMESTIC_TRUCK_TEMPLATE,
  SOFT_COMMODITIES_BULK_TEMPLATE,
  OIL_FUELS_BULK_TEMPLATE,
  CONSTRUCTION_BULK_TEMPLATE,
  METALLICS_BULK_TEMPLATE,
  SOFT_COMMODITIES_CONTAINER_TEMPLATE,
  OIL_FUELS_RAILWAY_TEMPLATE,
  CONSTRUCTION_TRUCK_TEMPLATE,
  METALLICS_CONTAINER_TEMPLATE,
  CONTAINER_FORWARDING_FULL_TEMPLATE,
  CONTAINER_LCL_CONSOLIDATION_TEMPLATE,
  TRUCK_FORWARDING_DOMESTIC_TEMPLATE,
  TRUCK_FORWARDING_CROSS_BORDER_TEMPLATE,
  RAILWAY_FORWARDING_DOMESTIC_TEMPLATE,
  RAILWAY_FORWARDING_INTERMODAL_TEMPLATE
} from '../types/contract';

export class TemplateService {
  // Hardcoded templates
  static getHardcodedTemplates(): Template[] {
    const now = new Date();
    return [
      {
        id: 'hardcoded-amazon-fba',
        name: 'Amazon FBA - Full Trade Cycle',
        description: 'Complete trade cycle from supplier to Amazon FBA warehouse',
        businessModelId: 'ecommerce-retail',
        categoryId: 'amazon-models',
        tasks: AMAZON_FBA_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Bulk'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-amazon-wholesale-domestic',
        name: 'Amazon Wholesale - Domestic Distribution',
        description: 'Domestic wholesale distribution to Amazon FBA warehouses',
        businessModelId: 'ecommerce-retail',
        categoryId: 'amazon-models',
        tasks: AMAZON_WHOLESALE_DOMESTIC_TEMPLATE,
        defaultTransportationVehicle: 'Container',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Container'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-shopify-direct-import',
        name: 'Shopify Store - Direct Import',
        description: 'Direct import from supplier to warehouse for Shopify store',
        businessModelId: 'ecommerce-retail',
        categoryId: 'shopify-models',
        tasks: SHOPIFY_DIRECT_IMPORT_TEMPLATE,
        defaultTransportationVehicle: 'Container',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Containerized', 'Container'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-dropshipping-per-order',
        name: 'Dropshipping - Per Order Execution',
        description: 'Per-order dropshipping process from customer order to delivery',
        businessModelId: 'ecommerce-retail',
        categoryId: 'dropshipping-models',
        tasks: DROPSHIPPING_PER_ORDER_TEMPLATE,
        defaultTransportationVehicle: 'Container',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Containerized', 'Container'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-port-agent-vessel-call',
        name: 'Port Agent - Vessel Call (General Cargo)',
        description: 'Complete port agency services for vessel call and general cargo operations',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'port-agency-forwarding',
        tasks: PORT_AGENT_VESSEL_CALL_TEMPLATE,
        defaultTransportationVehicle: 'General cargo',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Vessel', 'General cargo'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-port-agent-container-vessel',
        name: 'Port Agent - Container Vessel Call',
        description: 'Port agency services for container vessel operations and terminal handling',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'port-agency-forwarding',
        tasks: PORT_AGENT_CONTAINER_VESSEL_TEMPLATE,
        defaultTransportationVehicle: 'Container',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Container', 'Bulk'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-port-agent-tanker-vessel',
        name: 'Port Agent - Tanker Vessel Call',
        description: 'Port agency services for tanker vessel operations and oil terminal handling',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'port-agency-forwarding',
        tasks: PORT_AGENT_TANKER_VESSEL_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Bulk', 'Tanker'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-port-agent-bulk-carrier',
        name: 'Port Agent - Bulk Carrier Call',
        description: 'Port agency services for bulk carrier operations with draft surveys and laytime calculations',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'port-agency-forwarding',
        tasks: PORT_AGENT_BULK_CARRIER_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Bulk', 'General Cargo'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      // Manufacturing & Production Templates
      {
        id: 'hardcoded-industrial-equipment-international',
        name: 'Industrial Equipment - International Manufacturing',
        description: 'Complete international contract manufacturing process for industrial equipment',
        businessModelId: 'manufacturing-production',
        categoryId: 'industrial-equipment',
        tasks: INDUSTRIAL_EQUIPMENT_INTERNATIONAL_TEMPLATE,
        defaultTransportationVehicle: 'General cargo',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['General cargo'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-chemical-railway-transport',
        name: 'Chemical Production - Railway Transport',
        description: 'Chemical production and railway tank car transportation',
        businessModelId: 'manufacturing-production',
        categoryId: 'chemical-pharmaceutical',
        tasks: CHEMICAL_RAILWAY_TRANSPORT_TEMPLATE,
        defaultTransportationVehicle: 'Railway',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Railway'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-automotive-container-export',
        name: 'Automotive Parts - Container Export',
        description: 'Automotive parts manufacturing and container export process',
        businessModelId: 'manufacturing-production',
        categoryId: 'automotive-parts',
        tasks: AUTOMOTIVE_CONTAINER_EXPORT_TEMPLATE,
        defaultTransportationVehicle: 'Containerized',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Containerized'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-construction-project',
        name: 'Construction & Engineering - Project Manufacturing',
        description: 'Project-based manufacturing for construction and engineering materials',
        businessModelId: 'manufacturing-production',
        categoryId: 'construction-engineering',
        tasks: CONSTRUCTION_PROJECT_TEMPLATE,
        defaultTransportationVehicle: 'General cargo',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['General cargo'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-industrial-domestic-truck',
        name: 'Industrial Equipment - Domestic Production',
        description: 'Domestic industrial equipment manufacturing with truck delivery',
        businessModelId: 'manufacturing-production',
        categoryId: 'industrial-equipment',
        tasks: INDUSTRIAL_DOMESTIC_TRUCK_TEMPLATE,
        defaultTransportationVehicle: 'Truck',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Truck'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      // Bulk Commodities Trading Templates
      {
        id: 'hardcoded-soft-commodities-bulk',
        name: 'Soft Commodities - Bulk Vessel Trading',
        description: 'Bulk vessel trading for grains, oilseeds and agricultural commodities',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'soft-commodities',
        tasks: SOFT_COMMODITIES_BULK_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Bulk'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-oil-fuels-bulk',
        name: 'Oil & Fuels - Bulk Tanker Trading',
        description: 'Bulk tanker operations for oil, gas, and fuel products',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'oil-fuels',
        tasks: OIL_FUELS_BULK_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Bulk'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-construction-bulk',
        name: 'Construction Materials - Bulk Vessel',
        description: 'Bulk vessel transport for cement, chemicals and construction materials',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'construction-materials',
        tasks: CONSTRUCTION_BULK_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Bulk'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-metallics-bulk',
        name: 'Metallics & Steel - Bulk Vessel Trading',
        description: 'Bulk vessel trading for raw materials, metals and steel products',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'metallics-steel',
        tasks: METALLICS_BULK_TEMPLATE,
        defaultTransportationVehicle: 'Bulk',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Bulk'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-soft-commodities-container',
        name: 'Soft Commodities - Container Trading',
        description: 'Containerized trading for grains and agricultural products',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'soft-commodities',
        tasks: SOFT_COMMODITIES_CONTAINER_TEMPLATE,
        defaultTransportationVehicle: 'Containerized',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Containerized'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-oil-fuels-railway',
        name: 'Oil & Fuels - Railway Tank Car',
        description: 'Railway tank car transport for petroleum and chemical products',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'oil-fuels',
        tasks: OIL_FUELS_RAILWAY_TEMPLATE,
        defaultTransportationVehicle: 'Railway',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Railway'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-construction-truck',
        name: 'Construction Materials - Truck Transport',
        description: 'Truck delivery for construction materials and supplies',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'construction-materials',
        tasks: CONSTRUCTION_TRUCK_TEMPLATE,
        defaultTransportationVehicle: 'Truck',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Truck'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-metallics-container',
        name: 'Metallics & Steel - Container Export',
        description: 'Container export for steel products and metal materials',
        businessModelId: 'bulk-commodities-trading',
        categoryId: 'metallics-steel',
        tasks: METALLICS_CONTAINER_TEMPLATE,
        defaultTransportationVehicle: 'Containerized',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Containerized'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      // Agency & Forwarding Services Templates
      {
        id: 'hardcoded-container-forwarding-full',
        name: 'Container Forwarding - Full Service',
        description: 'Complete door-to-door container forwarding service',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'containers-forwarding',
        tasks: CONTAINER_FORWARDING_FULL_TEMPLATE,
        defaultTransportationVehicle: 'Container',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Container'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-container-lcl-consolidation',
        name: 'Container Forwarding - LCL Consolidation',
        description: 'Less than container load consolidation and forwarding services',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'containers-forwarding',
        tasks: CONTAINER_LCL_CONSOLIDATION_TEMPLATE,
        defaultTransportationVehicle: 'Container',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Container'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-truck-forwarding-domestic',
        name: 'Truck Forwarding - Domestic',
        description: 'Domestic truck forwarding and transportation services',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'trucks-forwarding',
        tasks: TRUCK_FORWARDING_DOMESTIC_TEMPLATE,
        defaultTransportationVehicle: 'Truck',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Truck'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-truck-forwarding-cross-border',
        name: 'Truck Forwarding - Cross-Border',
        description: 'International cross-border truck forwarding services',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'trucks-forwarding',
        tasks: TRUCK_FORWARDING_CROSS_BORDER_TEMPLATE,
        defaultTransportationVehicle: 'Truck',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Truck'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-railway-forwarding-domestic',
        name: 'Railway Forwarding - Domestic Rail',
        description: 'Domestic railway transportation and forwarding services',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'railway-forwarding',
        tasks: RAILWAY_FORWARDING_DOMESTIC_TEMPLATE,
        defaultTransportationVehicle: 'Railway',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: true,
        defaultTransportationMethods: ['Railway'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      },
      {
        id: 'hardcoded-railway-forwarding-intermodal',
        name: 'Railway Forwarding - Intermodal',
        description: 'Intermodal railway and truck combination forwarding services',
        businessModelId: 'agency-forwarding-services',
        categoryId: 'railway-forwarding',
        tasks: RAILWAY_FORWARDING_INTERMODAL_TEMPLATE,
        defaultTransportationVehicle: 'Railway',
        defaultStatusOptions: [
          'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled', 
          'Delayed', 'Under Review', 'Approved', 'Rejected', 'Awaiting Information'
        ],
        isActive: true,
        isDefault: false,
        isDefaultForCategory: false,
        defaultTransportationMethods: ['Railway'],
        createdBy: 'SYSTEM',
        createdAt: now,
        updatedAt: now,
        usageCount: 0
      }
    ];
  }

  // Business Models
  static async getAllBusinessModels(): Promise<BusinessModel[]> {
    try {
      const businessModelsRef = collection(db, 'businessModels');
      const snapshot = await getDocs(businessModelsRef);
      
      const businessModels: BusinessModel[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        businessModels.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as BusinessModel);
      });

      // If no business models exist, initialize with predefined ones
      if (businessModels.length === 0) {
        await this.initializeBusinessModels();
        return this.getAllBusinessModels();
      }

      return businessModels;
    } catch (error) {
      console.error('Error getting business models:', error);
      throw error;
    }
  }

  static async getBusinessModel(businessModelId: string): Promise<BusinessModel | null> {
    try {
      const docRef = doc(db, 'businessModels', businessModelId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as BusinessModel;
    } catch (error) {
      console.error('Error getting business model:', error);
      throw error;
    }
  }

  static async initializeBusinessModels(): Promise<void> {
    try {
      console.log('🔄 Initializing business models...');
      
      const { setDoc } = await import('firebase/firestore');
      
      for (const [id, modelData] of Object.entries(PREDEFINED_BUSINESS_MODELS)) {
        // Check if business model already exists
        const existing = await this.getBusinessModel(id);
        if (existing) {
          console.log(`✓ Business model already exists: ${modelData.name}`);
          continue;
        }

        const businessModel: Omit<BusinessModel, 'id'> = {
          name: modelData.name,
          description: modelData.description,
          icon: modelData.icon,
          transportationMethods: [...modelData.transportationMethods],
          categories: modelData.categories.map(cat => ({
            ...cat,
            businessModelId: id,
            isActive: true
          })),
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        // Use setDoc with specific document ID instead of addDoc
        await setDoc(doc(db, 'businessModels', id), {
          ...businessModel,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        console.log(`✅ Created business model: ${modelData.name}`);
      }
      
      console.log('✅ All business models initialized successfully');
    } catch (error) {
      console.error('Error initializing business models:', error);
      throw error;
    }
  }

  // Templates
  static async getAllTemplates(): Promise<Template[]> {
    try {
      const templatesRef = collection(db, 'templates');
      const snapshot = await getDocs(query(templatesRef, orderBy('createdAt', 'asc')));
      
      const templates: Template[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        templates.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Template);
      });

      return templates;
    } catch (error) {
      console.error('Error getting templates:', error);
      throw error;
    }
  }

  static async getTemplatesByBusinessModel(businessModelId: string): Promise<Template[]> {
    try {
      // Get Firebase templates
      const templatesRef = collection(db, 'templates');
      const q = query(
        templatesRef, 
        where('businessModelId', '==', businessModelId),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const firebaseTemplates: Template[] = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        firebaseTemplates.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Template);
      });

      // Get hardcoded templates for this business model
      const hardcodedTemplates = this.getHardcodedTemplates().filter(
        t => t.businessModelId === businessModelId
      );

      // Combine both lists
      const allTemplates = [...hardcodedTemplates, ...firebaseTemplates];

      // Sort in JavaScript to avoid Firestore index requirements
      allTemplates.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.createdAt.getTime() - b.createdAt.getTime();
      });

      console.log(`📋 Loaded ${hardcodedTemplates.length} hardcoded + ${firebaseTemplates.length} Firebase templates for ${businessModelId}`);

      return allTemplates;
    } catch (error) {
      console.error('Error getting templates by business model:', error);
      // If Firebase fails, still return hardcoded templates
      return this.getHardcodedTemplates().filter(
        t => t.businessModelId === businessModelId
      );
    }
  }

  static async getTemplate(templateId: string): Promise<Template | null> {
    try {
      const docRef = doc(db, 'templates', templateId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Template;
    } catch (error) {
      console.error('Error getting template:', error);
      throw error;
    }
  }

  static async createTemplate(template: Omit<Template, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'templates'), {
        ...template,
        usageCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Created template: ${template.name}`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  static async updateTemplate(templateId: string, updates: Partial<Template>): Promise<void> {
    try {
      const docRef = doc(db, 'templates', templateId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      console.log(`✅ Updated template: ${templateId}`);
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  static async deleteTemplate(templateId: string): Promise<void> {
    try {
      const docRef = doc(db, 'templates', templateId);
      await deleteDoc(docRef);
      
      console.log(`✅ Deleted template: ${templateId}`);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  static async incrementTemplateUsage(templateId: string): Promise<void> {
    try {
      const docRef = doc(db, 'templates', templateId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentUsage = docSnap.data().usageCount || 0;
        await updateDoc(docRef, {
          usageCount: currentUsage + 1,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error incrementing template usage:', error);
      // Don't throw error for usage tracking
    }
  }

  // Get default template for backward compatibility
  static async getDefaultTemplate(): Promise<Template | null> {
    try {
      const templatesRef = collection(db, 'templates');
      const q = query(
        templatesRef, 
        where('isDefault', '==', true),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Template;
    } catch (error) {
      console.error('Error getting default template:', error);
      return null;
    }
  }

  // NEW: Smart default template selection
  static async getSmartDefaultTemplate(
    categoryId?: string, 
    transportationMethod?: string
  ): Promise<Template | null> {
    try {
      console.log('🎯 Finding smart default for:', { categoryId, transportationMethod });

      // Priority 1: Category + Transportation Method match
      if (categoryId && transportationMethod) {
        const categoryTransportDefault = await this.getCategoryTransportationDefault(categoryId, transportationMethod);
        if (categoryTransportDefault) {
          console.log('✅ Found category+transport default:', categoryTransportDefault.name);
          return categoryTransportDefault;
        }
      }

      // Priority 2: Category default (any transportation method)
      if (categoryId) {
        const categoryDefault = await this.getCategoryDefault(categoryId);
        if (categoryDefault) {
          console.log('✅ Found category default:', categoryDefault.name);
          return categoryDefault;
        }
      }

      // Priority 3: Global default (backward compatibility)
      const globalDefault = await this.getDefaultTemplate();
      if (globalDefault) {
        console.log('✅ Found global default:', globalDefault.name);
        return globalDefault;
      }

      console.log('⚠️ No default template found');
      return null;
    } catch (error) {
      console.error('Error getting smart default template:', error);
      return null;
    }
  }

  // Get default template for specific category + transportation method
  static async getCategoryTransportationDefault(
    categoryId: string, 
    transportationMethod: string
  ): Promise<Template | null> {
    try {
      const templatesRef = collection(db, 'templates');
      const q = query(
        templatesRef,
        where('categoryId', '==', categoryId),
        where('isDefaultForCategory', '==', true),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      
      // Filter by transportation method in JavaScript (Firestore doesn't support array-contains with multiple where clauses)
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const template = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Template;

        // Check if this template applies to the requested transportation method
        if (template.defaultTransportationMethods?.includes(transportationMethod)) {
          return template;
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting category+transportation default:', error);
      return null;
    }
  }

  // Get default template for specific category (any transportation method)
  static async getCategoryDefault(categoryId: string): Promise<Template | null> {
    try {
      const templatesRef = collection(db, 'templates');
      const q = query(
        templatesRef,
        where('categoryId', '==', categoryId),
        where('isDefaultForCategory', '==', true),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }

      // Return the first category default found
      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Template;
    } catch (error) {
      console.error('Error getting category default:', error);
      return null;
    }
  }

  // Update existing templates to include new fields (migration helper)
  static async migrateExistingTemplates(): Promise<void> {
    try {
      console.log('🔄 Migrating existing templates...');
      
      const templatesRef = collection(db, 'templates');
      const snapshot = await getDocs(templatesRef);
      
      let migratedCount = 0;
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        // Check if template needs migration
        if (data.isDefaultForCategory === undefined || data.defaultTransportationMethods === undefined) {
          await updateDoc(doc.ref, {
            isDefaultForCategory: false,
            defaultTransportationMethods: [],
            updatedAt: serverTimestamp()
          });
          migratedCount++;
        }
      }
      
      console.log(`✅ Migrated ${migratedCount} templates`);
    } catch (error) {
      console.error('Error migrating templates:', error);
    }
  }
}
